import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { psAPI } from '../../../services/api';
import ProjectGantt from '../../../components/ps/ProjectGantt';
import { toast } from 'sonner';
import {
  FileText,
  BarChart3,
  TrendingUp,
  Calendar,
  RefreshCw,
  Download,
  DollarSign,
  Activity,
  Clock,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

export default function Reports() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [activeTab, setActiveTab] = useState('status');
  const [statusReport, setStatusReport] = useState(null);
  const [costReport, setCostReport] = useState(null);
  const [timelineData, setTimelineData] = useState(null);

  const [costReportParams, setCostReportParams] = useState({
    fiscal_year: new Date().getFullYear(),
    period_from: 1,
    period_to: 12,
    group_by: 'WBS',
    include_commitments: true
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await psAPI.getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateStatusReport = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }
    try {
      setReportLoading(true);
      const response = await psAPI.getProjectStatusReport({
        project_id: selectedProject,
        include_financials: true,
        include_schedule: true,
        include_milestones: true
      });
      setStatusReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleGenerateCostReport = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }
    try {
      setReportLoading(true);
      const response = await psAPI.getProjectCostReport({
        project_id: selectedProject,
        ...costReportParams
      });
      setCostReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleGenerateTimeline = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }
    try {
      setReportLoading(true);
      const response = await psAPI.getProjectTimeline(selectedProject);
      setTimelineData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate timeline');
    } finally {
      setReportLoading(false);
    }
  };

  const handleRescheduleItem = async (item, newStart, newFinish) => {
    try {
      await psAPI.rescheduleTimelineItem({
        item_type: item.type,
        item_id: item.id,
        planned_start: newStart,
        planned_finish: newFinish,
      });
      toast.success(`${item.name} rescheduled to ${newStart} → ${newFinish}`);
      const response = await psAPI.getProjectTimeline(selectedProject);
      setTimelineData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule');
      // refresh to revert optimistic visual
      try {
        const response = await psAPI.getProjectTimeline(selectedProject);
        setTimelineData(response.data);
      } catch { /* noop */ }
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const [exporting, setExporting] = useState('');

  const handleExport = async (kind, format) => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }
    try {
      setExporting(`${kind}-${format}`);
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      let response;
      if (kind === 'status') {
        response = await psAPI.exportProjectStatusReport(
          { project_id: selectedProject, include_financials: true, include_schedule: true, include_milestones: true },
          format
        );
      } else {
        response = await psAPI.exportProjectCostReport(
          { project_id: selectedProject, ...costReportParams },
          format
        );
      }
      downloadBlob(response.data, `project_${kind}_${selectedProject}.${ext}`);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setExporting('');
    }
  };

  const ExportButtons = ({ kind, disabled }) => (
    <div className="flex gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || !!exporting}
        onClick={() => handleExport(kind, 'pdf')}
        data-testid={`export-${kind}-pdf`}
      >
        <Download className="w-4 h-4 mr-1" />
        {exporting === `${kind}-pdf` ? 'PDF…' : 'PDF'}
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || !!exporting}
        onClick={() => handleExport(kind, 'excel')}
        data-testid={`export-${kind}-excel`}
      >
        <Download className="w-4 h-4 mr-1" />
        {exporting === `${kind}-excel` ? 'Excel…' : 'Excel'}
      </Button>
    </div>
  );

  const getStatusColor = (status) => {
    const colors = {
      'UNDER_BUDGET': 'text-emerald-600',
      'ON_BUDGET': 'text-orange-600',
      'OVER_BUDGET': 'text-red-600',
      'ON_TIME': 'text-emerald-600',
      'AHEAD': 'text-orange-600',
      'BEHIND': 'text-red-600'
    };
    return colors[status] || 'text-gray-600';
  };

  const formatCurrency = (value) => {
    return `$${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Project Reports</h1>
            <p className="text-gray-500">Generate and view project reports</p>
          </div>
        </div>

        {/* Project Selection */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>Select Project</Label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger data-testid="select-project">
                    <SelectValue placeholder="Choose a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="status" className="flex gap-2">
              <FileText className="w-4 h-4" />
              Status Report
            </TabsTrigger>
            <TabsTrigger value="cost" className="flex gap-2">
              <DollarSign className="w-4 h-4" />
              Cost Report
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex gap-2">
              <Calendar className="w-4 h-4" />
              Timeline
            </TabsTrigger>
          </TabsList>

          {/* Status Report Tab */}
          <TabsContent value="status" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-orange-600" />
                    Project Status Report
                  </span>
                  <div className="flex items-center gap-2">
                    <ExportButtons kind="status" disabled={!statusReport} />
                    <Button onClick={handleGenerateStatusReport} disabled={!selectedProject || reportLoading} data-testid="generate-status">
                      {reportLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                      Generate Report
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statusReport ? (
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <Label className="text-xs text-gray-500">Project</Label>
                          <p className="font-medium">{statusReport.project_name}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Type</Label>
                          <p>{statusReport.project_type}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Status</Label>
                          <Badge className="bg-orange-100 text-orange-700">{statusReport.project_status}</Badge>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Progress</Label>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-orange-600 h-2 rounded-full" 
                                style={{ width: `${statusReport.overall_progress || 0}%` }}
                              />
                            </div>
                            <span className="font-medium">{formatPercent(statusReport.overall_progress)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Financial Summary */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-orange-600" />
                        Financial Summary
                      </h3>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="p-4 bg-orange-50 rounded-lg">
                          <p className="text-sm text-orange-600">Budget</p>
                          <p className="text-xl font-bold text-orange-700">{formatCurrency(statusReport.total_budget)}</p>
                          <p className="text-xs text-orange-500">Released: {formatCurrency(statusReport.released_budget)}</p>
                        </div>
                        <div className="p-4 bg-orange-50 rounded-lg">
                          <p className="text-sm text-orange-600">Planned</p>
                          <p className="text-xl font-bold text-orange-700">{formatCurrency(statusReport.planned_costs)}</p>
                        </div>
                        <div className="p-4 bg-emerald-50 rounded-lg">
                          <p className="text-sm text-emerald-600">Actual</p>
                          <p className="text-xl font-bold text-emerald-700">{formatCurrency(statusReport.actual_costs)}</p>
                          <p className="text-xs text-emerald-500">Commitments: {formatCurrency(statusReport.commitments)}</p>
                        </div>
                        <div className={`p-4 rounded-lg ${statusReport.cost_variance >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                          <p className={`text-sm ${statusReport.cost_variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Variance</p>
                          <p className={`text-xl font-bold ${getStatusColor(statusReport.cost_status)}`}>
                            {formatCurrency(statusReport.cost_variance)}
                          </p>
                          <p className={`text-xs ${getStatusColor(statusReport.cost_status)}`}>
                            {formatPercent(statusReport.cost_variance_percent)} - {statusReport.cost_status}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Progress Summary */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <Activity className="w-4 h-4 text-orange-600" />
                          WBS Elements
                        </h3>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <p className="text-2xl font-bold">{statusReport.total_wbs_elements}</p>
                            <p className="text-xs text-gray-500">Total</p>
                          </div>
                          <div className="text-center p-3 bg-emerald-50 rounded-lg">
                            <p className="text-2xl font-bold text-emerald-600">{statusReport.completed_wbs_elements}</p>
                            <p className="text-xs text-emerald-500">Completed</p>
                          </div>
                          <div className="text-center p-3 bg-orange-50 rounded-lg">
                            <p className="text-2xl font-bold text-orange-600">{statusReport.in_progress_wbs_elements}</p>
                            <p className="text-xs text-orange-500">In Progress</p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-orange-600" />
                          Activities
                        </h3>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <p className="text-2xl font-bold">{statusReport.total_activities}</p>
                            <p className="text-xs text-gray-500">Total</p>
                          </div>
                          <div className="text-center p-3 bg-emerald-50 rounded-lg">
                            <p className="text-2xl font-bold text-emerald-600">{statusReport.completed_activities}</p>
                            <p className="text-xs text-emerald-500">Completed</p>
                          </div>
                          <div className="text-center p-3 bg-amber-50 rounded-lg">
                            <p className="text-2xl font-bold text-amber-600">{statusReport.overdue_activities || 0}</p>
                            <p className="text-xs text-amber-500">Overdue</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Generated timestamp */}
                    <div className="text-right text-sm text-gray-500">
                      Generated: {statusReport.generated_at ? new Date(statusReport.generated_at).toLocaleString() : '-'}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Select a project and generate the report</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cost Report Tab */}
          <TabsContent value="cost" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-orange-600" />
                    Project Cost Report
                  </span>
                  <div className="flex items-center gap-2">
                    <ExportButtons kind="cost" disabled={!costReport} />
                    <Button onClick={handleGenerateCostReport} disabled={!selectedProject || reportLoading} data-testid="generate-cost">
                      {reportLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                      Generate Report
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Parameters */}
                <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Fiscal Year</Label>
                    <Input
                      type="number"
                      value={costReportParams.fiscal_year}
                      onChange={(e) => setCostReportParams({ ...costReportParams, fiscal_year: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Period From</Label>
                    <Input
                      type="number"
                      value={costReportParams.period_from}
                      onChange={(e) => setCostReportParams({ ...costReportParams, period_from: parseInt(e.target.value) })}
                      min="1"
                      max="16"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Period To</Label>
                    <Input
                      type="number"
                      value={costReportParams.period_to}
                      onChange={(e) => setCostReportParams({ ...costReportParams, period_to: parseInt(e.target.value) })}
                      min="1"
                      max="16"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Group By</Label>
                    <Select value={costReportParams.group_by} onValueChange={(v) => setCostReportParams({ ...costReportParams, group_by: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="WBS">WBS Element</SelectItem>
                        <SelectItem value="COST_ELEMENT">Cost Element</SelectItem>
                        <SelectItem value="PERIOD">Period</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {costReport ? (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="grid grid-cols-6 gap-3">
                      <div className="p-3 bg-orange-50 rounded-lg text-center">
                        <p className="text-xs text-orange-600">Budget</p>
                        <p className="font-bold text-orange-700">{formatCurrency(costReport.total_budget)}</p>
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg text-center">
                        <p className="text-xs text-orange-600">Released</p>
                        <p className="font-bold text-orange-700">{formatCurrency(costReport.total_released)}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-600">Planned</p>
                        <p className="font-bold">{formatCurrency(costReport.total_planned)}</p>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-lg text-center">
                        <p className="text-xs text-emerald-600">Actual</p>
                        <p className="font-bold text-emerald-700">{formatCurrency(costReport.total_actual)}</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg text-center">
                        <p className="text-xs text-amber-600">Commitments</p>
                        <p className="font-bold text-amber-700">{formatCurrency(costReport.total_commitments)}</p>
                      </div>
                      <div className={`p-3 rounded-lg text-center ${costReport.total_variance >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                        <p className={`text-xs ${costReport.total_variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Variance</p>
                        <p className={`font-bold ${costReport.total_variance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {formatCurrency(costReport.total_variance)}
                        </p>
                      </div>
                    </div>

                    {/* Detail Table */}
                    {costReport.lines?.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left p-3">{costReport.group_by}</th>
                              <th className="text-right p-3">Budget</th>
                              <th className="text-right p-3">Released</th>
                              <th className="text-right p-3">Planned</th>
                              <th className="text-right p-3">Actual</th>
                              <th className="text-right p-3">Commit.</th>
                              <th className="text-right p-3">Available</th>
                              <th className="text-right p-3">Variance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {costReport.lines.map((line, idx) => (
                              <tr
                                key={`${line.group_id || line.group_name || idx}-${line.level || 0}`}
                                className="border-b hover:bg-gray-50"
                              >
                                <td className="p-3 font-medium" style={{ paddingLeft: `${(line.level || 0) * 16 + 12}px` }}>
                                  {line.group_name}
                                </td>
                                <td className="p-3 text-right">{formatCurrency(line.budget)}</td>
                                <td className="p-3 text-right">{formatCurrency(line.released_budget)}</td>
                                <td className="p-3 text-right">{formatCurrency(line.planned_costs)}</td>
                                <td className="p-3 text-right">{formatCurrency(line.actual_costs)}</td>
                                <td className="p-3 text-right">{formatCurrency(line.commitments)}</td>
                                <td className={`p-3 text-right ${line.available >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {formatCurrency(line.available)}
                                </td>
                                <td className={`p-3 text-right ${line.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {formatCurrency(line.variance)} ({formatPercent(line.variance_percent)})
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Select a project and generate the report</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-orange-600" />
                    Project Timeline
                  </span>
                  <Button onClick={handleGenerateTimeline} disabled={!selectedProject || reportLoading} data-testid="generate-timeline">
                    {reportLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                    Generate Timeline
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {timelineData ? (
                  <div className="space-y-4">
                    {/* Timeline Header */}
                    <div className="p-4 bg-gray-50 rounded-lg grid grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Project</Label>
                        <p className="font-medium">{timelineData.project_name}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Start</Label>
                        <p>{timelineData.earliest_start}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Finish</Label>
                        <p>{timelineData.latest_finish}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Items</Label>
                        <p>{timelineData.total_items} ({timelineData.critical_path_items} critical)</p>
                      </div>
                    </div>

                    {/* Interactive Gantt chart */}
                    <ProjectGantt data={timelineData} onReschedule={handleRescheduleItem} />
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Select a project and generate the timeline</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
